/**
 * clientDatevApi — WL-K3 client wrappers for the Kanzlei DATEV-Stammdaten callables.
 *
 * Thin httpsCallable bindings (same pattern as {@link ./clientInviteApi}). All hard
 * security checks live server-side (`functions/src/tenant/clientDatevConfig.ts`):
 * Loop-B-Beta of the caller + an active `advisor` membership in the mandant scope,
 * plus the strict field whitelist. These helpers only marshal input/output and
 * normalise errors for the UI. There is NO FE env flag — visibility is gated with
 * `canUseAdvisorDatev(...)` (M-GA-4; `GA_FLAG || isLoopBBeta`) at the component level.
 *
 * @module api/clientDatevApi
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebaseConfig';
import type { ClientDatevConfigView, DatevSkr } from '../utils/clientDatevFormat';

/** Partial config payload — only present fields are changed server-side. */
export interface ClientDatevConfigInput {
  datevBeraterNr?: string | null;
  datevMandantenNr?: string | null;
  datevSkr?: DatevSkr | null;
  datevSachkontenLaenge?: number | null;
}

/** Server result of {@link setClientDatevConfig}. */
export interface SetClientDatevConfigResult {
  status: 'ok';
  updated: string[];
}

/**
 * Server result of {@link getClientDatevConfig}.
 *
 * `isSmallBusiness` (WL-K7 FIX v3) is the mandant's server-authoritative §19-UStG
 * status — a SIBLING of `config`, not part of the Stammdaten-Ampel. Consumed by
 * `AdvisorDatevPage` so it stops hardcoding `false` into `loadMandantDatevSource`
 * (GA-Gate #17).
 */
export interface GetClientDatevConfigResult {
  config: ClientDatevConfigView;
  isSmallBusiness: boolean;
}

/**
 * Reads back the four `sellers.datev*` fields of a mandant (read-only) so the form
 * can pre-populate and drive its „exportbereit?" indicator.
 *
 * @param mandantUid the mandant (tenant owner) uid.
 */
export async function getClientDatevConfig(
  mandantUid: string,
): Promise<GetClientDatevConfigResult> {
  const callable = httpsCallable<{ mandantUid: string }, GetClientDatevConfigResult>(
    functions,
    'getClientDatevConfig',
  );
  try {
    const res = await callable({ mandantUid });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Sets (overwriting) the DATEV master data of a mandant. Only the fields present in
 * `config` are changed; a `null` value clears that field. The server rejects unknown
 * keys and malformed values with `invalid-argument`.
 *
 * @param mandantUid the mandant (tenant owner) uid.
 * @param config     the (partial) DATEV field values to set.
 */
export async function setClientDatevConfig(
  mandantUid: string,
  config: ClientDatevConfigInput,
): Promise<SetClientDatevConfigResult> {
  const callable = httpsCallable<
    { mandantUid: string; config: ClientDatevConfigInput },
    SetClientDatevConfigResult
  >(functions, 'setClientDatevConfig');
  try {
    const res = await callable({ mandantUid, config });
    return res.data;
  } catch (err) {
    throw normalizeCallableError(err);
  }
}

/**
 * Maps a Firebase callable error to a plain Error, preserving the server message
 * and exposing the coarse function error code (e.g. `permission-denied`).
 */
function normalizeCallableError(err: unknown): Error & { code?: string } {
  const anyErr = err as { code?: string; message?: string };
  const message =
    typeof anyErr?.message === 'string' && anyErr.message.length > 0
      ? anyErr.message
      : 'Request failed.';
  const out = new Error(message) as Error & { code?: string };
  if (typeof anyErr?.code === 'string') {
    out.code = anyErr.code.replace(/^functions\//, '');
  }
  return out;
}
